<template>
  <div class="select-market-demo">
    <div class="select-market-demo__selection-area">
      <h5 class="center">Current Uploaded Market Demos Selection: {{fileUploaded}}</h5>
      <table>
        <thead>
        <tr>
          <th v-if="isNpower" class="market-break">Market Break</th>
          <th class="demo">Demographic</th>
          <th>Target Audience</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="md in marketDemoOptions">
          <td v-if="isNpower"><span>{{md.marketBreak}}</span></td>
          <td><span>{{md.demographic}}</span></td>
          <td class="center"><input type="checkbox" v-model="md.isSelected"/></td>
        </tr>
        </tbody>
      </table>
    </div>
    <div class="select-market-demo__results-area">
      <span>DATA TO UPLOAD</span>
      <div v-if="hasDataCollisions" class="select-market-demo__error">
        <icon name="times-circle"></icon>
        <span>{{errorMessage}}</span>
      </div>
      <div v-if="hasOverwrites" class="select-market-demo__warning">
        <icon name="warning"></icon>
        <span><b>{{overwriteCount.toLocaleString()}}</b>{{warningMessage}}</span>
      </div>
      <table>
        <thead>
        <tr>
          <th v-if="isNpower" class="market-break">Market Break</th>
          <th class="demo">Demographic</th>
          <th>Average UE (000)</th>
          <th>New Records</th>
          <th>Existing Records</th>
          <th>% of Complete Half Hours</th>
          <th>Network</th>
          <th>Quarter</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="md in marketDemoSegments">
          <td v-if="isNpower"><span>{{md.MarketBreak}}</span></td>
          <td><span>{{md.Demographic}}</span></td>
          <td><span>{{Math.floor(md.AverageUEs/1000).toLocaleString()}}</span></td>
          <td><span>{{md.NumberOfRecords.toLocaleString()}}</span></td>
          <td>
            <span>{{existingQuarterNetworks[md.NetworkName + md.QuarterName] || 0}}</span>
            <icon v-if="existingQuarterNetworks[md.NetworkName + md.QuarterName]" class="select-market-demo__warning" name="warning"></icon>
          </td>
          <td><span>{{getRecordCompletionPercent(md.NumberOfRecords, md.NumberOfExpectedRecords)}}</span></td>
          <td><span>{{md.NetworkName}}</span></td>
          <td><span>{{md.QuarterName}}</span></td>
          <td class="select-market-demo__error" v-if="selectedQuarterNetworks[md.NetworkName + md.QuarterName].length > 1">
            <icon name="times-circle"></icon>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
    <div class="select-market-demo__upload-segments center">
      <action-button :disabled="!allowUpload" @click.native="uploadSegments" label="Upload Segments" color="success" id="uploadSegmentsBtn"/>
    </div>
    <div class="select-market-demo__note-area">
      <span>NOTE: Audience must have 12 months of data & must be sent downstream from the dashboard</span>
    </div>
  </div>
</template>

<script>
  import {flatten} from '@/helpers'
  import 'vue-awesome/icons/times-circle'
  import 'vue-awesome/icons/warning'
  import Icon from 'vue-awesome/components/Icon'
  import ActionButton from '@/components/common/actionButton'
  import {UPLOAD_NPOWER_SEGMENTS} from '@/appLogic/targets/createTarget/store/actions'
  import {LAS_TYPES} from '@/appLogic/targets/Constants'
  import {round} from 'lodash'

  export default {
    components: {
      Icon,
      ActionButton
    },
    props: {
      onCompleteSegmentUpload: {
        type: Function,
        default: () => {}
      },
      segmentData: { type: Object, default: () => ({ MarketDemos: [] }) },
      currentSegments: { type: Array, default: () => [] },
      uploadedTargetId: String,
      uploadedTargetViewingStream: String,
      fileUploaded: String
    },
    data () {
      return {
        errorMessage: 'The chosen audience segments overlap on this network(s) and quarter(s). Please see the table below for the overlaps.',
        warningMessage: ` records already exist for this audience segment on these network(s) and quarter(s). Any records in this file which overlap on a half hour will be overwritten.`,
        selectedFile: null,
        marketDemoOptions: []
      }
    },
    mounted () {
      this.setMarketDemoOptions()
    },
    computed: {
      target () {
        return {
          display_name: '',
          target_datasource: null,
          advertiser: null,
          target: null,
          username: this.$store.state.auth.username
        }
      },
      marketDemoSegments () {
        const selected = this.marketDemoOptions.filter(d => d.isSelected)
        const marketDemos = this.segmentData.MarketDemos

        const segments = selected
          .map(s => marketDemos.filter(md => md.MarketBreak === s.marketBreak && md.Demographic === s.demographic)
            .map(md => Object.assign({ index: s.index }, md))
          )
        return flatten(segments)
      },
      selectedQuarterNetworks () {
        const quarterNetworks = {}
        this.marketDemoSegments.forEach(md => {
          const key = md.NetworkName + md.QuarterName
          const quarterNet = quarterNetworks[key]
          if (!quarterNet) quarterNetworks[key] = [md.index]
          else {
            if (!quarterNet.includes(md.index)) quarterNet.push(md.index)
          }
        })
        return quarterNetworks
      },
      existingQuarterNetworks () {
        const quarterNetworks = {}
        this.currentSegments.forEach(segment => {
          const key = segment.NetworkName + segment.QuarterName
          quarterNetworks[key] = segment.NumberOfRecords
        })
        return quarterNetworks
      },
      hasDataCollisions () {
        const selected = this.selectedQuarterNetworks
        return Object.keys(selected).findIndex(qn => selected[qn].length > 1) > -1
      },
      hasOverwrites () {
        const selected = this.selectedQuarterNetworks
        return Object.keys(selected).findIndex(qn => this.existingQuarterNetworks[qn]) > -1
      },
      overwriteCount () {
        const selected = this.selectedQuarterNetworks
        return Object.keys(selected).reduce((prev, curr) => prev + (this.existingQuarterNetworks[curr] || 0), 0)
      },
      allowUpload () {
        return this.marketDemoSegments > [] && !this.hasDataCollisions
      },
      isNpower () {
        return this.segmentData.DataProvider === LAS_TYPES.NPOWER
      }
    },
    methods: {
      setMarketDemoOptions () {
        const marketDemoOptions = []
        const marketDemos = this.segmentData.MarketDemos
        marketDemos.forEach(demo => {
          // must set TargetAudience to true in order for new segments to be picked up by Iris + CAE
          demo.TargetAudience = true
          if (marketDemoOptions.findIndex(d => d.marketBreak === demo.MarketBreak && d.demographic === demo.Demographic) > -1) return

          marketDemoOptions.push({ marketBreak: demo.MarketBreak, demographic: demo.Demographic })
        })
        // sort checkbox options alphabetically by market break, then demographic
        marketDemoOptions.sort((a, b) => {
          const marketBreakA = a.marketBreak.toUpperCase()
          const marketBreakB = b.marketBreak.toUpperCase()

          if (marketBreakA < marketBreakB) return -1
          if (marketBreakA > marketBreakB) return 1

          const demoA = a.demographic.toUpperCase()
          const demoB = b.demographic.toUpperCase()

          if (demoA < demoB) return -1
          if (demoA > demoB) return 1
          return 0
        })
        this.marketDemoOptions = marketDemoOptions.map((md, idx) => Object.assign({ index: idx }, md))
      },
      uploadSegments () {
        const username = this.$store.state.auth.username
        const UploadedDateTime = new Date().toISOString()

        const remainingCurrentMarketDemos = this.currentSegments.filter(md => {
          const key = md.NetworkName + md.QuarterName
          return this.selectedQuarterNetworks[key] === undefined
        })
        // prune 'index' field, add 'username', and update 'UploadedDateTime' for new market demo segments (stored by AM)
        const newMarketDemos = this.marketDemoSegments.map(({ index, ...marketDemo }) => ({ username, ...marketDemo, UploadedDateTime }))
        const allMarketDemos = newMarketDemos.concat(remainingCurrentMarketDemos)

        // prune 'username' field before uploading to Iris
        const MarketDemos = newMarketDemos.map(({ username, ...marketDemo }) => marketDemo)

        const segmentData = Object.assign({}, this.segmentData, { MarketDemos, ViewingStream: this.uploadedTargetViewingStream.toUpperCase() })
        const payload = { segmentData, marketDemos: allMarketDemos, target_id: this.uploadedTargetId, username }
        this.$store.dispatch(UPLOAD_NPOWER_SEGMENTS, payload).then(res => this.onCompleteSegmentUpload())
      },
      getRecordCompletionPercent (records, expected) {
        return round(records / expected, 2) * 100 + '%'
      }
    },
    watch: {
      segmentData () {
        this.setMarketDemoOptions()
      }
    }
  }

</script>

<style lang="scss">

  /* need to remove modal__content height (height) and modal__container width (max-width) restrictions */
  /* height should be unlimited, width should be ~1000px */

  /* TODO:
      - add tooltip for cols exceeding max length
      - display warning icon next to 'New Records' cell if NumberOfRecords != num of half-hours in quarter
  */

  .select-market-demo {
    height: 100%;
    width: 100%;
    color: black;

    .center {
      display: grid;
      justify-content: center;
      align-content: center;
    }

    &__selection-area {
      > table {
        table-layout: fixed;
        width: 100%;
        margin-bottom: 20px;
      }
      th.market-break, th.demo { width: 45%; }

      > span {
        display: grid;
        justify-content: center;
        align-content: center;
      }
    }

    &__results-area {
      > table {
        table-layout: fixed;
        width: 100%;
      }

      th.market-break, th.demo { width: 25%; }

      > span {
        display: grid;
        justify-content: center;
        align-content: center;
        color: black;
        margin-bottom: 20px;
        font-weight: bold;
      }
    }

    &__upload-segments {
      color: green;
      margin: 10px;
    }

    &__note-area {
      display: grid;
      justify-content: center;
      align-content: center;
      color: black;
      margin-bottom: 20px;
    }

    &__error {
      color: red;
      margin-bottom: 10px;
      margin-left: 5px;
    }

    &__warning {
      color: #ff8c00;
      margin: 5px 0 0 5px
    }

    .truncate-text {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    th {
      background: #d3d3d3;
    }
    td {
      font-weight: normal;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    td, th {
      box-shadow: 0 0 0 1px;
      min-height: 24px;
    }
    @media (min-resolution: 2dppx) {
      td, th {
        box-shadow: 0 0 0 0.5px;
      }
    }
    @media (min-resolution: 3dppx) {
      td, th {
        box-shadow: 0 0 0 0.33333333px;
      }
    }
    @media (min-resolution: 4dppx) {
      td, th {
        box-shadow: 0 0 0 0.25px;
      }
    }
  }

</style>



// WEBPACK FOOTER //
// NpowerSelectSegmentModal.vue?120950fe